import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-4c1c27b9"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "toggle-container" }

import { computed, ref, inject, watch } from "vue";
import modalBase from "@/components/ui/modals/modal-base.vue";
import { IDDescriptionPair } from "@/types/generic/IDDescriptionPair";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import displayErrorMessage from "@/utils/ErrorHandling";
import { LookupTableSource } from "acquaint-api/constants/LookupTableSource";
import GridBase from "@/components/ui/grid/grid-base.vue";
import { DxColumn } from "devextreme-vue/data-grid";
import { DxToolbarItem } from "devextreme-vue/popup";
import { displayQuestionAlert } from "@/alerts/questionAlert";
import SwitchToggle from "./ui/toggles/switch-toggle.vue";
import { useUserPermissionsStore } from "@/stores/UserPermissionsStore";
import { UserPermissions } from "@/types/user/permissions/UserPermissions";
import NewEditSmsTextsTemplatesModal from "./new-edit-sms-texts-templates-modal.vue";
import { getErrorMessage } from "bright-logic-core/src/errors";
import { SMSTexts } from "acquaint-api/models/SMSTexts";
import { TemplateContentTypes } from "@/enums/TemplateContentTypes";


export default /*#__PURE__*/_defineComponent({
  __name: 'sms-texts-configuration-templates',
  props: {
  /**
   * The boolean value used with `v-model` to control the visibility or state of a modal or component.
   * @type {boolean}
   * @required
   */
  modelValue: {
    type: Boolean,
    required: true,
  },
  /**
   * Determines whether to accessed the templage from Configuration Screen.
   * @type {boolean}
   * @default true
   */
  accessedFromConfigurationScreen: {
    type: Boolean,
    default: true,
  },
  /**
   * Determines whether the template for EnMasse texting.
   * @type {boolean}
   * @default true
   */
  templateForEnMasseTexting: {
    type: Boolean,
    default: false,
  },
},
  emits: [
  /**
   * Triggered when the `modelValue` changes. This is used to update the v-model variable in the parent component.
   * @property {boolean} value - Indicates whether the modal has been opened or closed.
   */
  "update:modelValue",
  /** Triggered when one of the SMS Text templates has been selected, and returns the template ID with the marketing flag  */
  "templateSelected",
],
  setup(__props, { emit }) {

const props = __props;




const showModal = computed({
  get: () => props.modelValue,
  set: (value: Boolean) => emit("update:modelValue", value),
});

const userPermissionsStore = useUserPermissionsStore();
const acquaintApiClient = inject("AcquaintApiClient") as AcquaintApiClient;
const sMSTextsList = ref<IDDescriptionPair[]>([]);
const showInactiveItems = ref(false);
const userPermissions = ref<UserPermissions>();
const editNewModalId = ref(0);
const selectedConfigurationItem = ref<IDDescriptionPair>({
  id: 0,
  description: "",
});
const displayEditNewModal = ref(false);
const templateData = ref<SMSTexts>();

const disableEdit = computed(() => {
  return selectedConfigurationItem.value.id == 0;
});

const showInactive = ref(0);

const fetchConfigurationList = async () => {
  try {
    sMSTextsList.value = await acquaintApiClient.httpGet<IDDescriptionPair[]>(
      `app/system/lookup/${LookupTableSource.SMSTemplates}?includeinactive=${showInactiveItems.value}`,
    );
  } catch (err) {
    displayErrorMessage("The selected details could not be fetched");
    sMSTextsList.value = [];
  }
};

watch(
  () => showModal.value,
  async () => {
    if (showModal.value) {
      userPermissions.value = await userPermissionsStore.getUserPermissions();
      fetchConfigurationList();
    }
  },
);

const configurationItemSelected = (e: any) => {
  if (!e) {
    return;
  }
  selectedConfigurationItem.value = e;
};

const configurationItemDoubleClicked = async (e: any) => {
  if (!e) {
    return;
  }

  selectedConfigurationItem.value = e;

  if (props.accessedFromConfigurationScreen) {
    editNewModalId.value = selectedConfigurationItem.value.id;
    displayEditNewModal.value = true;
  } else {
    if (props.templateForEnMasseTexting) {
      await fetchTemplateData();
    }
    emit(
      "templateSelected",
      selectedConfigurationItem.value.id,
      templateData.value?.contentTypeID == TemplateContentTypes.Marketing,
      selectedConfigurationItem.value.description,
    );
    showModal.value = false;
  }
};

const openEditModal = () => {
  if (
    !userPermissions.value ||
    !userPermissions.value.contactsConfiguration.edit
  ) {
    displayErrorMessage(
      "Sorry, you do not have the permission to edit these details.",
    );
    return;
  }

  editNewModalId.value = selectedConfigurationItem.value.id;
  displayEditNewModal.value = true;
};

const openNewModal = () => {
  if (
    !userPermissions.value ||
    !userPermissions.value.contactsConfiguration.new
  ) {
    displayErrorMessage(
      "Sorry, you do not have the permission to create a new system item.",
    );

    return;
  }

  editNewModalId.value = 0;
  displayEditNewModal.value = true;
};

const deleteConfigurationItem = async () => {
  if (
    !userPermissions.value ||
    !userPermissions.value.contactsConfiguration.delete
  ) {
    displayErrorMessage(
      "Sorry, you do not have the permission to delete these details.",
    );
    return;
  }

  if (selectedConfigurationItem.value.id < 1000) {
    displayErrorMessage(
      "Sorry, you can't delete this system item as it is required by Acquaint. However you may edit it, and set it as Inactive to remove it from this list.",
    );
    return;
  }

  const question = await displayQuestionAlert(
    "Are you sure you want to delete these details?",
  );

  if (!question.isConfirmed) {
    return;
  }

  if (await callDeletApi()) {
    selectedConfigurationItem.value = { id: 0, description: "" };
    fetchConfigurationList();
  }
};

const callDeletApi = async () => {
  try {
    await acquaintApiClient.httpDeleteVoid(
      `database/smstexts/${selectedConfigurationItem.value.id}`,
    );
    return true;
  } catch (err) {
    displayErrorMessage(
      "Sorry, these details cannot be deleted as they are referenced elsewhere.",
    );
    return false;
  }
};

const inactiveToggleValueChange = async () => {
  showInactiveItems.value = showInactive.value ? true : false;
  await fetchConfigurationList();

  if (showInactive.value) {
    return;
  }

  const findInactiveItem = sMSTextsList.value?.find((item) => {
    return item.id == selectedConfigurationItem.value.id;
  });

  if (!findInactiveItem) {
    selectedConfigurationItem.value = { id: 0, description: "" };
  }
};

const newItemCreated = () => {
  selectedConfigurationItem.value = { id: 0, description: "" };
  fetchConfigurationList();
};

const editItem = (inActive: number) => {
  if (inActive && !showInactiveItems.value) {
    selectedConfigurationItem.value = { id: 0, description: "" };
  }

  fetchConfigurationList();
};

const onSelectButtonClicked = async () => {
  if (props.templateForEnMasseTexting) {
    await fetchTemplateData();
  }

  emit(
    "templateSelected",
    selectedConfigurationItem.value.id,
    templateData.value?.contentTypeID == TemplateContentTypes.Marketing,
    selectedConfigurationItem.value.description,
  );
  showModal.value = false;
};

const fetchTemplateData = async () => {
  try {
    templateData.value = await acquaintApiClient.httpGet(
      `database/smstexts/${selectedConfigurationItem.value.id}`,
    );
  } catch (err) {
    templateData.value = undefined;
    displayErrorMessage(getErrorMessage(err));
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(modalBase, {
      title: "SMS Text Templates",
      modelValue: showModal.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showModal).value = $event)),
      "show-close-button": true,
      height: "460px"
    }, {
      "non-scrollable-content": _withCtx(() => [
        _createVNode(GridBase, {
          dataSource: sMSTextsList.value,
          onRowClick: configurationItemDoubleClicked,
          onRowSelected: configurationItemSelected,
          onAdaptiveRowSelection: configurationItemSelected,
          "id-column-name": "id",
          showColumnHeaders: false,
          "pre-selected-id": selectedConfigurationItem.value.id
        }, {
          "header-content": _withCtx(() => [
            _createElementVNode("div", _hoisted_1, [
              _createVNode(SwitchToggle, {
                label: "Show Inactive",
                modelValue: showInactive.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((showInactive).value = $event)),
                readOnly: false,
                onOnVaueChange: inactiveToggleValueChange,
                "margin-bottom": "0rem"
              }, null, 8, ["modelValue"])
            ])
          ]),
          columns: _withCtx(() => [
            _createVNode(_unref(DxColumn), {
              "data-field": "description",
              caption: "",
              "hiding-priority": 8,
              width: "100%"
            }),
            _createVNode(_unref(DxColumn), {
              "data-field": "id",
              caption: "Id",
              "hiding-priority": 1
            })
          ]),
          _: 1
        }, 8, ["dataSource", "pre-selected-id"])
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Select',
          visible: !props.accessedFromConfigurationScreen,
          disabled: disableEdit.value,
          onClick: () => {
            onSelectButtonClicked();
          },
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Delete',
          visible: props.accessedFromConfigurationScreen,
          disabled: disableEdit.value,
          onClick: () => {
            deleteConfigurationItem();
          },
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Edit',
          visible: props.accessedFromConfigurationScreen,
          disabled: disableEdit.value,
          onClick: () => {
            openEditModal();
          },
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'New',
          visible: props.accessedFromConfigurationScreen,
          onClick: () => {
            openNewModal();
          },
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Cancel',
          onClick: () => {
            showModal.value = false;
          },
        }
        }, null, 8, ["options"])
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(NewEditSmsTextsTemplatesModal, {
      modelValue: displayEditNewModal.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((displayEditNewModal).value = $event)),
      configurationId: editNewModalId.value,
      onConfigurationItemCreated: newItemCreated,
      onConfigurationItemEdited: editItem
    }, null, 8, ["modelValue", "configurationId"])
  ], 64))
}
}

})